import React, { useEffect, useState } from 'react';
import { CircularProgress, Popper, TextField } from '@mui/material';
import { Autocomplete } from '@mui/material';

const PopperMy = function (props: any) {
  return (
    <Popper
      data-cy="popper-select-model-popup"
      {...props}
      placement="bottom-start"
      modifiers={{ flip: { enabled: false } }}
    />
  );
};

interface Option {
  id: string;
  label: string;
}

interface SelectModelProps {
  items: Option[];
  onChangeCallback: (value?: string) => void;
  defaultValue?: string;
  disabled?: boolean;
  style?: any;
  setSearch: (value: string) => void;
  loading?: boolean;
  totalItems?: number;
}

const SelectModel: React.FC<SelectModelProps> = ({
  items,
  onChangeCallback,
  defaultValue,
  disabled,
  style,
  setSearch,
  loading,
  totalItems = 0,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState(items);
  const [hasDefaultItemBeenSet, setHasDefaultItemBeenSet] = useState(false);
  const [value, setValue] = useState<Option | null>(() => {
    return items.find((o) => o.id === defaultValue) || null;
  });

  const [open, setOpen] = useState(false);

  useEffect(() => {
    let active = true;

    if (loading) {
      return undefined;
    }

    if (active) {
      setOptions(items);
    }

    // Set default value for the same model when items are loaded
    // Check if defaultValue exists in items and hasn't been set yet
    if (active && !hasDefaultItemBeenSet && defaultValue && items.length > 0) {
      const defaultItem = items.find((o) => o.id === defaultValue);
      if (defaultItem) {
        setValue(defaultItem);
        setHasDefaultItemBeenSet(true);
      }
    }

    return () => {
      active = false;
    };
  }, [items, setOptions, loading, defaultValue, hasDefaultItemBeenSet]);

  // Debounce search input - use the selected value's label if available, otherwise use typed input
  useEffect(() => {
    const timer = setTimeout(() => {
      const searchValue = value?.label || inputValue;
      setSearch(searchValue);
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [inputValue, value, setSearch]);

  useEffect(() => {
    onChangeCallback(value?.id);
  }, [value, onChangeCallback]);

  return (
    <Autocomplete
      id="select-model"
      style={style}
      disabled={disabled}
      data-cy="select-training-set-model"
      defaultValue={options.find((o) => {
        return o.id === defaultValue;
      })}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => {
        if (defaultValue === option.id) {
          return `[Same Model] ${option.label}`;
        }

        return option.label;
      }}
      PopperComponent={PopperMy}
      value={value}
      onChange={(event: any, newValue: Option | null) => {
        setValue(newValue);
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search models..."
          variant="outlined"
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

export default SelectModel;
